import React, { useState, useEffect } from "react";
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { questionnaireService } from '../../services/questionnaireService';
import { PatientQuestionnaireWithAnswers } from '../../types/questionnaire.types';
import { useTenant } from '../../context/TenantContext';
import { MedicationMonitoringFlag } from '../../services/patientService';

interface ViewQuestionnaireModalProps {
  isOpen: boolean;
  onClose: () => void;
  questionnaireId: number;
  questionnaireTitle: string;
}

export const ViewQuestionnaireModal: React.FC<ViewQuestionnaireModalProps> = ({
  isOpen,
  onClose,
  questionnaireId,
  questionnaireTitle,
}) => {
  const { currentTenant } = useTenant();
  const [questionnaire, setQuestionnaire] = useState<PatientQuestionnaireWithAnswers | null>(null);
  const [flags, setFlags] = useState<MedicationMonitoringFlag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Question names the monitoring rules flagged, matched trimmed — several field
  // names in these definitions carry stray surrounding spaces.
  const flaggedNames = new Set(flags.map((f) => f.question_name.trim()));

  useEffect(() => {
    const fetchQuestionnaireDetails = async () => {
      if (!isOpen || !questionnaireId || !currentTenant) return;

      setIsLoading(true);
      setError(null);
      try {
        const data = await questionnaireService.getPatientQuestionnaire(
          questionnaireId,
          currentTenant.id
        );
        setQuestionnaire(data);
        // Best-effort: the answers are the point of this popup, so a failure here
        // just means no highlighting.
        questionnaireService
          .getQuestionnaireMonitoringFlags(questionnaireId, currentTenant.id)
          .then(setFlags)
          .catch(() => setFlags([]));
      } catch (err: any) {
        console.error("Failed to load questionnaire details:", err);
        setError("Failed to load questionnaire details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestionnaireDetails();
  }, [isOpen, questionnaireId, currentTenant]);

  if (!isOpen) return null;

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={questionnaireTitle}
      isLoading={isLoading}
      width="!max-w-4xl"
    >
      {/* Modal Content */}
      <div className="overflow-y-auto px-3 py-3 max-h-[60vh]">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75] mx-auto"></div>
              <p className="text-[#366A75] mt-4 text-sm">Loading questionnaire...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : questionnaire && questionnaire.answers && Array.isArray(questionnaire.answers) ? (
          <div className="space-y-3" dir={(questionnaire.questionnaire_type as any)?.language === 'he' ? 'rtl' : 'ltr'}>
            {flags.length > 0 && (
              <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-red-800 mb-2">
                  Medication monitoring red flag{flags.length === 1 ? '' : 's'}
                </h3>
                <ul className="list-disc list-inside space-y-1">
                  {flags.map((flag) => (
                    <li key={flag.code} className="text-sm text-red-700">{flag.label}</li>
                  ))}
                </ul>
              </div>
            )}
            {(() => {
              // Sort answers by order, filter out empty answers, and display
              return [...questionnaire.answers]
                .sort((a, b) => a.order - b.order)
                .filter((answerObj) => {
                  // Filter out empty answers
                  const answer = answerObj.answer as any;
                  if (!answer) return false;
                  if (Array.isArray(answer) && answer.length === 0) return false;
                  if (typeof answer === 'string' && answer.trim() === '') return false;
                  return true;
                })
                .map((answerObj, index) => {
                  const questionTitle = answerObj.question_text || answerObj.question_name;

                  // Format answer value
                  let answerDisplay: React.ReactNode;

                  if (Array.isArray(answerObj.answer)) {
                    // Parse JSON-string file items (stored as '{"id":80,"name":"...","size":...}')
                    const parsedItems = answerObj.answer.map((item: any) => {
                      if (typeof item === 'string') {
                        try { return JSON.parse(item); } catch { return item; }
                      }
                      return item;
                    });

                    // Check if this is a file upload field (contains objects with id, name, size)
                    const hasFileObjects = parsedItems.length > 0 &&
                      typeof parsedItems[0] === 'object' &&
                      parsedItems[0] !== null &&
                      'id' in parsedItems[0] &&
                      'name' in parsedItems[0];

                    if (hasFileObjects) {
                      answerDisplay = (
                        <span className="text-gray-700">
                          {parsedItems.map((item: any) => item.name).join(', ')}
                        </span>
                      );
                    } else {
                      // Regular array items
                      const answerValue = answerObj.answer
                        .map((item) => {
                          if (typeof item === 'object' && item !== null && 'name' in item) {
                            return item.name;
                          }
                          return String(item);
                        })
                        .join(', ');
                      answerDisplay = <span className="text-gray-700">{answerValue}</span>;
                    }
                  } else {
                    answerDisplay = <span className="text-gray-700">{String(answerObj.answer)}</span>;
                  }

                  const isFlagged = flaggedNames.has(String(answerObj.question_name).trim());

                  return (
                    <div
                      key={index}
                      className={isFlagged
                        ? 'bg-red-50 border border-red-300 rounded-lg p-4 shadow-sm'
                        : 'bg-white rounded-lg p-4 shadow-sm'}
                    >
                      <div className="text-gray-800 text-sm leading-relaxed">
                        {isFlagged && (
                          <span
                            className="inline-block mr-2 px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-bold align-middle"
                            title="Flagged by medication monitoring"
                          >
                            !
                          </span>
                        )}
                        <span className="font-medium">{questionTitle}: </span>
                        {answerDisplay}
                      </div>
                    </div>
                  );
                });
            })()}

            {/* Scores Section */}
            {questionnaire.scores && Object.keys(questionnaire.scores).length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-300">
                <h3 className="text-base font-semibold text-gray-800 mb-3">Scores</h3>
                <div className="space-y-2">
                  {Object.entries(questionnaire.scores).map(([key, value], index) => (
                    <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-gray-800 text-sm leading-relaxed">
                        <span className="font-medium">{key}: </span>
                        <span className="text-gray-700">{value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-[#366A75]/60">
            <p className="text-sm">No questionnaire data available</p>
          </div>
        )}
      </div>

      {/* Modal Footer */}
      <div className="flex justify-center gap-4 mt-6">
        <button
          onClick={onClose}
          className={POPUP_STYLES.button}
        >
          Close
        </button>
      </div>
    </ModalWrapper>
  );
};
